import ical from 'ical'
import { RRule } from 'rrule'
import { CalendarEvent } from 'rrule-duration'
import { v4 as uuid } from 'uuid'

import type { Moment } from './moment'
import moment from './moment'

type AvailabilityRuleInit = {
  id?: string
  start: Moment
  end: Moment
  until?: Moment
  repeats?: boolean
}

export type AvailabilityRuleOccurrence = {
  start: Moment
  end: Moment
}

export enum AvailabilityRuleIntersectionType {
  /**
   *__|----|
   *
   *__|----|
   */
  Precise = 'Precise',
  /**
   *___|--|
   *
   *__|----|
   */
  Cover = 'Cover',
  /**
   *__|----|
   *
   *___|--|
   */
  Inside = 'Inside',
  /**
   *_____|----|
   *
   *__|----|
   */
  Overlap = 'Overlap',
  /**
   *__________|----|
   *
   *__|----|
   */
  None = 'None',
}

export class AvailabilityRule {
  id: string
  start: Moment
  end: Moment
  until?: Moment
  tzid: string
  private event: CalendarEvent

  constructor({ id = uuid(), start, end, until, repeats = false }: AvailabilityRuleInit) {
    this.id = id
    this.start = start
    this.end = end

    this.tzid = start.tz() ?? 'UTC'
    this.until = repeats && until ? until : undefined

    const rrule = (() => {
      if (repeats) {
        const dtstart = this.toNormalized(start)

        // UNTIL is an instant: read on the rule's clock, as dtstart is, before rrule sees it as local.
        const runtil = this.until ? this.toNormalized(this.until.clone().tz(this.tzid)) : undefined

        const rule = new RRule({
          dtstart: dtstart.toDate(),
          freq: RRule.WEEKLY,
          until: runtil?.toDate(),
        })

        let maxSearch = dtstart.clone().add(1, 'month')
        maxSearch = moment.min(runtil ?? maxSearch, maxSearch)

        if (rule.between(dtstart.toDate(), maxSearch.toDate(), true).length !== 1) {
          return rule
        }
      }

      return new RRule({
        dtstart: this.toNormalized(start).toDate(),
      })
    })()

    if (!rrule.options.freq) {
      this.until = undefined
    }

    this.event = new CalendarEvent({
      start: { dateTime: this.toNormalized(start).toDate() },
      end: { dateTime: this.toNormalized(end, start).toDate() },
      recurrences: [rrule],
    })
  }

  private get utStart() {
    return this.toNormalized(this.start)
  }

  private get utEnd() {
    return this.toNormalized(this.start)
  }

  get data(): AvailabilityRuleInit {
    return {
      id: this.id,
      start: this.start.clone(),
      end: this.end.clone(),
      until: this.until?.clone(),
      repeats: this.repeats,
    }
  }

  update(data?: Partial<AvailabilityRuleInit>) {
    if (data?.until && !data.repeats && !this.data.repeats) throw new Error('Setting until to non-repeating event')
    let rule = new AvailabilityRule({ ...this.data, ...data })
    if (rule.until && rule.occurrencesBetween(rule.start, rule.until).length === 1) {
      rule = rule.update({ until: undefined, repeats: false })
    }
    return rule
  }

  clone() {
    return this.update()
  }

  occurrencesBetween(start: Moment, end: Moment): AvailabilityRuleOccurrence[] {
    const ofs = start.clone().tz(this.tzid, false).local(true).subtract(1, 'minute')
    const ofe = end.clone().tz(this.tzid, false).local(true)

    if (!this.repeats) {
      return this.utStart.isBetween(ofs, ofe) || this.utEnd.isBetween(ofs, ofe) ? [{ start: this.start.clone(), end: this.end.clone() }] : []
    }

    const daysDiff = this.end.clone().startOf('day').diff(this.start.clone().startOf('day'), 'days')

    return (
      this.event
        .occurences({ between: [ofs.toDate(), ofe.toDate()] })
        .map(([start, end]) => ({ start: this.toOriginal(start), end: this.toOriginal(end) }))
        // rrule-calendar may set date of end to wrong date
        .map(({ start, end }) => ({ start, end: start.clone().add(daysDiff, 'days').hours(end.hours()).minutes(end.minutes()).seconds(end.seconds()) }))
    )
  }

  occurrencesUntil(end: Moment): AvailabilityRuleOccurrence[] {
    return this.occurrencesBetween(this.start, end)
  }

  /** Rule has no occurrences */
  isEmpty(start: Moment, end: Moment) {
    return this.occurrencesBetween(start, end).length === 0
  }

  get repeats() {
    return this.rrule && !!this.rrule.options.freq
  }

  get rrule() {
    return (this.event['recurrences']?._rrule as RRule[] | undefined)?.at(0)
  }

  get rruleString() {
    if (!this.rrule) return undefined
    return this.getRRuleString(this.rrule)
  }

  toString() {
    return [
      'BEGIN:AVAILABLE',
      `UID:${this.id}`,
      `DTSTART;TZID=${this.start.tz()}:${this.start.format('YYYYMMDDTHHmmss')}`,
      `DTEND;TZID=${this.start.tz()}:${this.end.format('YYYYMMDDTHHmmss')}`,
      this.repeats ? this.rruleString : '',
      'END:AVAILABLE',
    ]
      .filter(l => !!l)
      .join('\n')
  }

  private toNormalized(date: Moment, base?: Moment) {
    date = date.clone()

    const basetz = base?.tz()
    if (basetz) date.tz(basetz)

    return date.local(true)
  }

  private toOriginal(date: Moment | Date) {
    const offset = moment(this.start).local().utcOffset()
    return this.setTimezone(moment(date).utc(false).add(offset, 'minutes').utcOffset(moment(date).utcOffset(), true), this.tzid)
  }

  private setTimezone(date: Moment, timezone: string) {
    return moment.tz(date.format('YYYY-MM-DDTHH:mm:ss'), timezone)
  }

  static parseIcs = (def: string): AvailabilityRule => {
    const parsed = Object.values(ical.parseICS(def)).at(0) as unknown as ParsedVRule
    if (!parsed) throw new Error("Can't parse availability rule")
    if (parsed.type !== 'AVAILABLE') throw new Error(`Wrong type of parsed rule: ${parsed.type}`)
    let start = moment(parsed.start).local(false)
    if ('tz' in parsed.start) {
      start = start.tz(parsed.start.tz as string, true)
    }
    let end = moment(parsed.end).local(false)
    if ('tz' in parsed.end) {
      end = end.tz(parsed.end.tz as string, true)
    }
    const rrule = (() => {
      if (!parsed.rrule) return undefined
      const rruleString = parsed.rrule
      const rrule = RRule.fromString(rruleString)
      AvailabilityRule.assertRuleValid(rrule)
      return rrule
    })()

    return new AvailabilityRule({
      id: parsed.uid,
      start,
      end,
      until: rrule?.options.until ? moment.utc(rrule.options.until) : undefined,
      repeats: !!rrule?.options.freq,
    })
  }

  public static firstIntersection(
    range: [Moment, Moment],
    ruleA: AvailabilityRule,
    ruleB: AvailabilityRule
  ): [AvailabilityRuleOccurrence, AvailabilityRuleOccurrence] | undefined {
    const occsA = ruleA.occurrencesBetween(range[0], range[1])
    const occsB = ruleB.occurrencesBetween(range[0], range[1])

    if (!occsA.length || !occsB.length) return undefined

    for (const occA of occsA) {
      for (const occB of occsB) {
        const [ocA, ocB] = [occA, occB].sort((a, b) => a.start.diff(b.start))
        switch (true) {
          case ocB.start.isSameOrAfter(ocA.start) && ocB.start.isBefore(ocA.end):
          case ocB.end.isSameOrAfter(ocA.start) && ocB.end.isBefore(ocA.end): {
            return [occA, occB]
          }
          case true:
          default:
            break
        }
      }
    }

    return undefined
  }

  public static getIntersectionType(ruleA: AvailabilityRuleOccurrence, ruleB: AvailabilityRuleOccurrence): AvailabilityRuleIntersectionType {
    switch (true) {
      case ruleA.start.isSame(ruleB.start) && ruleA.end.isSame(ruleB.end):
        return AvailabilityRuleIntersectionType.Precise
      case ruleA.start.isSameOrBefore(ruleB.start) && ruleA.end.isSameOrAfter(ruleB.end):
        return AvailabilityRuleIntersectionType.Inside
      case ruleA.start.isSameOrAfter(ruleB.start) && ruleA.end.isSameOrBefore(ruleB.end):
        return AvailabilityRuleIntersectionType.Cover
      case ruleA.start.isBefore(ruleB.start) && ruleA.end.isAfter(ruleB.start):
        return AvailabilityRuleIntersectionType.Overlap
      case ruleA.start.isBefore(ruleB.end) && ruleA.end.isAfter(ruleB.end):
        return AvailabilityRuleIntersectionType.Overlap
      case true:
      default:
        return AvailabilityRuleIntersectionType.None
    }
  }

  private static assertRuleValid(rule: RRule) {
    const rstr = AvailabilityRule.getRRuleString(rule)
    if (!/RRULE:FREQ=WEEKLY(;UNTIL=)?/.test(rstr)) throw new Error(`Invalid rule: ${rstr}`)
  }

  private getRRuleString(rule: RRule) {
    rule = rule.clone()
    if (this.until) {
      rule.origOptions.until = this.until.toDate()
    }
    return AvailabilityRule.getRRuleString(rule)
  }

  private static getRRuleString(rule: RRule) {
    return rule
      .toString()
      .split('\n')
      .find(s => s.startsWith('RRULE:'))!
  }
}

export type AvailabilityRuleSlot = {
  id: string
  start: string
  end: string
  repeats?: boolean
}

type ParsedVRule = {
  type: 'AVAILABLE'
  uid: string
  start: Date
  end: Date
  rrule?: string
}
